<template>
  <header
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      scrolled
        ? 'bg-mlb-navy/95 backdrop-blur-md shadow-lg shadow-black/30 border-b border-mlb-navy-light/30'
        : isLanding
          ? 'bg-transparent border-b border-transparent'
          : 'bg-mlb-navy border-b border-mlb-navy-light/20',
      isNative ? 'safe-top' : ''
    ]"
  >
    <div class="max-w-7xl mx-auto px-4">
      <div class="flex items-center justify-between h-16">

        <!-- Logo -->
        <router-link :to="authStore.isAuthenticated ? authStore.homeRoute : '/'" class="flex items-center gap-3 text-white no-underline shrink-0 group">
          <BrandLogo :size="36" glow class="text-white" />
          <div class="hidden sm:block">
            <span class="block text-base font-black tracking-wide leading-none" style="font-family: var(--font-display)">SOFTBALL STATS CLUB</span>
            <span class="block text-[10px] text-mlb-gold font-semibold tracking-[0.15em] leading-none mt-0.5" style="font-family: var(--font-display)">YOUR ANALYTICS HUB</span>
          </div>
        </router-link>

        <!-- Desktop Navigation -->
        <nav class="hidden md:flex items-center gap-0.5 text-sm">

          <template v-if="authStore.isAuthenticated">

          <!-- Dashboard -->
          <router-link
            to="/dashboard"
            class="nav-link"
            :class="{ 'nav-link-active': route.path === '/dashboard' }"
          >
            <LayoutDashboard class="w-3.5 h-3.5" />
            Dashboard
          </router-link>

          <!-- My Team (switcher when the user has several teams) -->
          <div v-if="activeTeamId" class="relative group/team" data-tour="team-switcher">
            <router-link
              :to="`/teams/${activeTeamId}`"
              class="nav-link"
              :class="{ 'nav-link-active': route.path.startsWith('/teams/') && !route.path.includes('/admin') }"
            >
              <Users class="w-3.5 h-3.5" />
              {{ activeTeamName || 'My Team' }}
              <ChevronDown v-if="switcherTeams.length > 1" class="w-3 h-3 opacity-60 transition-transform group-hover/team:rotate-180" />
            </router-link>
            <div
              v-if="switcherTeams.length > 1"
              class="absolute top-full left-0 pt-2 opacity-0 invisible group-hover/team:opacity-100 group-hover/team:visible transition-all duration-200 z-50"
            >
              <div class="bg-white dark:bg-[#0f1f3d] shadow-2xl rounded-xl min-w-[200px] py-1.5 border border-gray-100 dark:border-[#1e3a5f] overflow-hidden">
                <div class="h-0.5 bg-gradient-to-r from-mlb-gold via-mlb-gold-light to-mlb-gold mb-1.5" />
                <button
                  v-for="t in switcherTeams"
                  :key="t.team_id"
                  @click="selectTeam(t.team_id)"
                  class="dropdown-item mx-1 w-[calc(100%-8px)]"
                  :class="{ 'font-bold': t.team_id === activeTeamId }"
                >
                  <Users class="w-4 h-4 text-mlb-navy dark:text-blue-400" />
                  {{ t.team_name }}
                </button>
              </div>
            </div>
          </div>

          <!-- My Stats (everyone) -->
          <router-link
            to="/my-stats"
            class="nav-link"
            :class="{ 'nav-link-active': route.path === '/my-stats' }"
          >
            <BarChart2 class="w-3.5 h-3.5" />
            My Stats
          </router-link>

          <!-- Stats entry (coaches and admins only) -->
          <router-link
            v-if="authStore.isCoach || authStore.isAdmin"
            to="/stats"
            class="nav-link"
            :class="{ 'nav-link-active': route.path === '/stats' }"
          >
            <BarChart2 class="w-3.5 h-3.5" />
            Stats
          </router-link>

          <!-- Manage dropdown -->
          <div v-if="authStore.isCoach || authStore.isAdmin" class="relative group/manage" data-tour="manage-menu">
            <button class="nav-link group-hover/manage:bg-white/10 group-hover/manage:text-white">
              <Settings class="w-3.5 h-3.5" />
              Manage
              <ChevronDown class="w-3 h-3 opacity-60 transition-transform group-hover/manage:rotate-180" />
            </button>
            <div class="absolute top-full right-0 pt-2 opacity-0 invisible group-hover/manage:opacity-100 group-hover/manage:visible transition-all duration-200 z-50">
              <div class="bg-white dark:bg-[#0f1f3d] shadow-2xl rounded-xl min-w-[220px] py-1.5 border border-gray-100 dark:border-[#1e3a5f] overflow-hidden">
                <!-- Gold accent bar -->
                <div class="h-0.5 bg-gradient-to-r from-mlb-gold via-mlb-gold-light to-mlb-gold mb-1.5" />
                <router-link
                  v-if="activeTeamId"
                  :to="`/teams/${activeTeamId}/invites`"
                  class="dropdown-item mx-1"
                >
                  <UserPlus class="w-4 h-4 text-mlb-red" />
                  Invite Players
                </router-link>
                <router-link
                  v-if="activeTeamId"
                  :to="`/teams/${activeTeamId}/emails`"
                  class="dropdown-item mx-1"
                >
                  <Mail class="w-4 h-4 text-mlb-red" />
                  Coach Jeter Emails
                </router-link>
                <router-link
                  v-if="activeTeamId"
                  :to="`/teams/${activeTeamId}/opponents`"
                  class="dropdown-item mx-1"
                >
                  <Swords class="w-4 h-4 text-mlb-red" />
                  Opponents
                </router-link>
                <template v-if="authStore.isAdmin">
                  <div class="border-t border-gray-100 dark:border-[#1e3a5f] my-1.5 mx-2" />
                  <div class="px-3 py-1">
                    <span class="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500" style="font-family: var(--font-display)">Administration</span>
                  </div>
                  <router-link to="/admin/seasons" class="dropdown-item mx-1">
                    <Calendar class="w-4 h-4 text-mlb-navy dark:text-blue-400" />
                    Seasons
                  </router-link>
                  <router-link to="/admin/venues" class="dropdown-item mx-1">
                    <MapPin class="w-4 h-4 text-mlb-navy dark:text-blue-400" />
                    Venues
                  </router-link>
                  <router-link to="/admin/teams" class="dropdown-item mx-1">
                    <Shield class="w-4 h-4 text-mlb-navy dark:text-blue-400" />
                    Create Team
                  </router-link>
                  <router-link to="/admin/users" class="dropdown-item mx-1">
                    <Users class="w-4 h-4 text-mlb-navy dark:text-blue-400" />
                    Users
                  </router-link>
                </template>
              </div>
            </div>
          </div>

          </template>

          <template v-else>
            <router-link
              :to="{ path: '/', hash: '#features' }"
              class="nav-link"
            >
              <Sparkles class="w-3.5 h-3.5" />
              Features
            </router-link>
            <router-link
              to="/demo"
              class="nav-link"
              :class="{ 'nav-link-active': route.path === '/demo' }"
            >
              <MonitorPlay class="w-3.5 h-3.5" />
              Live Demo
            </router-link>
          </template>

          <!-- Divider -->
          <div class="w-px h-5 bg-white/15 mx-1.5" />

          <ThemeToggle />

          <!-- Authenticated user menu -->
          <template v-if="authStore.isAuthenticated">
            <div class="relative group/user ml-1">
              <button class="flex items-center gap-2 pl-2 pr-1.5 py-1.5 rounded-lg text-blue-100 hover:text-white hover:bg-white/10 transition-all">
                <div class="relative">
                  <div class="w-8 h-8 rounded-full bg-gradient-to-br from-mlb-gold to-mlb-gold-light flex items-center justify-center text-xs font-black text-mlb-navy-deep shadow-sm" style="font-family: var(--font-display)">
                    {{ initials }}
                  </div>
                  <div class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-mlb-navy" />
                </div>
                <ChevronDown class="w-3 h-3 opacity-60 transition-transform group-hover/user:rotate-180" />
              </button>
              <div class="absolute top-full right-0 pt-2 opacity-0 invisible group-hover/user:opacity-100 group-hover/user:visible transition-all duration-200 z-50">
                <div class="bg-white dark:bg-[#0f1f3d] shadow-2xl rounded-xl min-w-[200px] py-1.5 border border-gray-100 dark:border-[#1e3a5f] overflow-hidden">
                  <div class="h-0.5 bg-gradient-to-r from-mlb-gold via-mlb-gold-light to-mlb-gold mb-1.5" />
                  <div class="px-4 py-2.5">
                    <div class="flex items-center gap-2.5">
                      <div class="w-9 h-9 rounded-full bg-gradient-to-br from-mlb-gold to-mlb-gold-light flex items-center justify-center text-sm font-black text-mlb-navy-deep shrink-0" style="font-family: var(--font-display)">{{ initials }}</div>
                      <div class="min-w-0">
                        <div class="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate">{{ fullName }}</div>
                        <span class="badge-navy text-[10px] px-1.5 py-0.5">{{ roleLabel }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="border-t border-gray-100 dark:border-[#1e3a5f] my-1" />
                  <router-link to="/profile" class="dropdown-item mx-1">
                    <UserCircle class="w-4 h-4 text-gray-400" />
                    Profile
                  </router-link>
                  <div class="border-t border-gray-100 dark:border-[#1e3a5f] my-1" />
                  <button @click="authStore.logout()" class="dropdown-item mx-1 w-[calc(100%-8px)] text-mlb-red hover:bg-red-50 dark:hover:bg-red-900/20">
                    <LogOut class="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <router-link to="/login" class="ml-1 btn-primary h-8 px-4 text-xs">
              Sign In
            </router-link>
          </template>
        </nav>

        <!-- Mobile: right side -->
        <div class="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            class="text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            @click="toggleMobileMenu"
            aria-label="Toggle menu"
          >
            <component :is="mobileMenuOpen ? X : Menu" class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile overlay -->
    <Transition name="overlay">
      <div
        v-if="mobileMenuOpen"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden"
        @click="closeMobileMenu"
      />
    </Transition>

    <!-- Mobile drawer (slides from left) -->
    <Transition name="drawer">
      <div
        v-if="mobileMenuOpen"
        class="fixed top-0 left-0 w-4/5 max-w-xs h-screen z-40 md:hidden overflow-y-auto"
        style="background: linear-gradient(160deg, #001a3a 0%, #002D62 60%, #1a4480 100%)"
      >
        <!-- Drawer header -->
        <div class="flex items-center gap-3 px-5 pt-6 pb-5 border-b border-white/10">
          <BrandLogo :size="36" glow class="text-white" />
          <div>
            <div class="text-white font-black text-sm tracking-wide" style="font-family: var(--font-display)">SOFTBALL STATS CLUB</div>
            <div class="text-mlb-gold text-[10px] font-semibold tracking-widest" style="font-family: var(--font-display)">YOUR ANALYTICS HUB</div>
          </div>
          <button @click="closeMobileMenu" class="ml-auto text-white/60 hover:text-white p-1">
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- User info -->
        <div v-if="authStore.isAuthenticated" class="mx-4 mt-4 mb-2 p-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-mlb-gold to-mlb-gold-light flex items-center justify-center text-sm font-black text-mlb-navy-deep shrink-0" style="font-family: var(--font-display)">{{ initials }}</div>
          <div>
            <div class="text-white text-sm font-semibold">{{ fullName }}</div>
            <span class="badge-gold text-[10px]">{{ roleLabel }}</span>
          </div>
        </div>

        <!-- Nav links -->
        <nav class="flex flex-col px-3 mt-3 gap-0.5">
          <template v-if="!authStore.isAuthenticated">
            <router-link to="/" class="mobile-nav-link" @click="closeMobileMenu">
              <LayoutDashboard class="w-4 h-4" />
              Home
            </router-link>
            <router-link :to="{ path: '/', hash: '#features' }" class="mobile-nav-link" @click="closeMobileMenu">
              <Sparkles class="w-4 h-4" />
              Features
            </router-link>
            <router-link to="/demo" class="mobile-nav-link" @click="closeMobileMenu">
              <MonitorPlay class="w-4 h-4" />
              Live Demo
            </router-link>
          </template>
          <template v-else>
          <router-link to="/dashboard" class="mobile-nav-link" @click="closeMobileMenu">
            <LayoutDashboard class="w-4 h-4" />
            Dashboard
          </router-link>
          <router-link v-if="activeTeamId" :to="`/teams/${activeTeamId}`" class="mobile-nav-link" @click="closeMobileMenu">
            <Users class="w-4 h-4" />
            {{ activeTeamName || 'My Team' }}
          </router-link>
          <template v-if="switcherTeams.length > 1">
            <div class="px-3 pt-2 pb-1">
              <span class="text-[10px] font-bold uppercase tracking-widest text-blue-300/60" style="font-family: var(--font-display)">Switch Team</span>
            </div>
            <button
              v-for="t in switcherTeams"
              :key="t.team_id"
              @click="selectTeam(t.team_id)"
              class="mobile-nav-link text-left"
              :class="{ 'text-mlb-gold': t.team_id === activeTeamId }"
            >
              <Users class="w-4 h-4" />
              {{ t.team_name }}
            </button>
          </template>
          <router-link to="/my-stats" class="mobile-nav-link" @click="closeMobileMenu">
            <BarChart2 class="w-4 h-4" />
            My Stats
          </router-link>
          <router-link v-if="authStore.isCoach || authStore.isAdmin" to="/stats" class="mobile-nav-link" @click="closeMobileMenu">
            <BarChart2 class="w-4 h-4" />
            Player Stats
          </router-link>

          <template v-if="authStore.isCoach || authStore.isAdmin">
            <div class="px-3 pt-4 pb-1">
              <span class="text-[10px] font-bold uppercase tracking-widest text-blue-300/60" style="font-family: var(--font-display)">Manage</span>
            </div>
            <router-link v-if="activeTeamId" :to="`/teams/${activeTeamId}/invites`" class="mobile-nav-link" @click="closeMobileMenu">
              <UserPlus class="w-4 h-4" />
              Invite Players
            </router-link>
            <router-link v-if="activeTeamId" :to="`/teams/${activeTeamId}/emails`" class="mobile-nav-link" @click="closeMobileMenu">
              <Mail class="w-4 h-4" />
              Coach Jeter Emails
            </router-link>
            <router-link v-if="activeTeamId" :to="`/teams/${activeTeamId}/opponents`" class="mobile-nav-link" @click="closeMobileMenu">
              <Swords class="w-4 h-4" />
              Opponents
            </router-link>
            <template v-if="authStore.isAdmin">
              <router-link to="/admin/seasons" class="mobile-nav-link" @click="closeMobileMenu">
                <Calendar class="w-4 h-4" />
                Seasons
              </router-link>
              <router-link to="/admin/venues" class="mobile-nav-link" @click="closeMobileMenu">
                <MapPin class="w-4 h-4" />
                Venues
              </router-link>
              <router-link to="/admin/teams" class="mobile-nav-link" @click="closeMobileMenu">
                <Shield class="w-4 h-4" />
                Create Team
              </router-link>
              <router-link to="/admin/users" class="mobile-nav-link" @click="closeMobileMenu">
                <Users class="w-4 h-4" />
                Users
              </router-link>
            </template>
          </template>
          </template>
        </nav>

        <!-- Bottom actions -->
        <div class="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-mlb-navy-deep/80 backdrop-blur-sm">
          <template v-if="authStore.isAuthenticated">
            <button @click="handleLogoutMobile" class="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-mlb-red/40 text-mlb-red hover:bg-mlb-red/10 transition-colors text-sm font-semibold">
              <LogOut class="w-4 h-4" />
              Sign Out
            </button>
          </template>
          <template v-else>
            <router-link to="/login" @click="closeMobileMenu" class="btn-primary w-full justify-center">
              Sign In
            </router-link>
          </template>
        </div>
      </div>
    </Transition>
  </header>

  <!-- Spacer so content isn't hidden under fixed header (the landing hero runs beneath it) -->
  <div v-if="!isLanding" class="h-16" :class="isNative ? 'safe-top' : ''" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  Menu, X, ChevronDown, LogOut, Users, BarChart2, Settings,
  UserPlus, Calendar, MapPin, Shield, UserCircle, LayoutDashboard, Mail, Swords,
  Sparkles, MonitorPlay
} from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { usePlatform } from '@/composables/usePlatform'
import { apiClient } from '@/api/client'
import ThemeToggle from '@/components/common/ThemeToggle.vue'
import BrandLogo from '@/components/common/BrandLogo.vue'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const { isNative } = usePlatform()

const mobileMenuOpen = ref(false)
const scrolled = ref(false)

// The landing hero runs beneath a transparent header until the page scrolls.
const isLanding = computed(() => route.name === 'Home')

const activeTeamId = computed(() => authStore.activeTeamId)

// Admins have no team permissions, so their switcher list is fetched.
const adminTeams = ref<{ team_id: number; team_name: string }[]>([])
const switcherTeams = computed(() =>
  authStore.isAdmin ? adminTeams.value : authStore.myTeams
)
const activeTeamName = computed(() =>
  switcherTeams.value.find(t => t.team_id === activeTeamId.value)?.team_name ?? ''
)

function selectTeam(teamId: number) {
  authStore.switchTeam(teamId)
  closeMobileMenu()
  router.push(`/teams/${teamId}`)
}

onMounted(async () => {
  if (authStore.isAuthenticated && authStore.isAdmin) {
    try {
      const res = await apiClient.get('/teams')
      adminTeams.value = (res.data ?? []).map((t: any) => ({
        team_id: Number(t.team_id),
        team_name: t.team_name,
      }))
      // Heal a missing OR stale stored team id (e.g. the team was deleted).
      const validIds = adminTeams.value.map(t => t.team_id)
      if (
        adminTeams.value.length > 0 &&
        (!authStore.activeTeamId || !validIds.includes(authStore.activeTeamId))
      ) {
        authStore.switchTeam(adminTeams.value[0].team_id)
      }
    } catch {
      // Switcher is a convenience — ignore load failures.
    }
  }
})

const fullName = computed(() => {
  const u = authStore.userInfo
  if (!u) return 'User'
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`
  return u.firstName || u.username || 'User'
})

const initials = computed(() => {
  const u = authStore.userInfo
  if (!u) return '?'
  const f = u.firstName?.[0] ?? ''
  const l = u.lastName?.[0] ?? ''
  return (f + l).toUpperCase() || u.username?.[0]?.toUpperCase() || '?'
})

const roleLabel = computed(() => {
  if (authStore.isAdmin) return 'Administrator'
  if (authStore.isCoach) return 'Coach'
  return 'Player'
})

function handleScroll() {
  scrolled.value = window.scrollY > 8
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
  document.body.style.overflow = mobileMenuOpen.value ? 'hidden' : ''
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
  document.body.style.overflow = ''
}

const handleLogoutMobile = () => {
  closeMobileMenu()
  authStore.logout()
}

onMounted(() => window.addEventListener('scroll', handleScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', handleScroll))
</script>

<style scoped>
.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  color: #bfdbfe;
  border-radius: 0.5rem;
  font-weight: 500;
  position: relative;
  transition: all 0.15s;
  font-size: 0.8125rem;
}

.nav-link:hover {
  color: white;
  background: rgba(255,255,255,0.1);
}

.nav-link-active {
  color: white;
  background: rgba(255,255,255,0.1);
}

.nav-link-active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0.75rem;
  right: 0.75rem;
  height: 2px;
  background: #C89B3C;
  border-radius: 9999px;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.75rem;
  color: rgba(255,255,255,0.8);
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.15s;
}

.mobile-nav-link:hover {
  color: white;
  background: rgba(255,255,255,0.1);
}

/* Drawer transition */
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(-100%);
}

/* Overlay transition */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
</style>
